/**
 * The operator frontend.
 *
 * Sidebar navigation over three pages, routed by hash so a device is linkable.
 */

import { useCallback, useEffect, useState } from 'react';

import { AcceptInvite } from './AcceptInvite.tsx';
import { fetchClients, fetchSites, storedToken, storeToken } from './api.ts';
import { ClaimCode } from './ClaimCode.tsx';
import { Clients } from './Clients.tsx';
import { DeviceDetail } from './DeviceDetail.tsx';
import { DeviceList, type IssuedClaim } from './DeviceList.tsx';
import { Login } from './Login.tsx';
import { Overview } from './Overview.tsx';
import { Provision } from './Provision.tsx';
import { isPublicRoute, parseRoute, type Route } from './routing.ts';
import { Shell } from './Shell.tsx';
import { Audit } from './Audit.tsx';
import { Users } from './Users.tsx';
import { useDevices } from './useDevices.ts';

const PAGE_TITLES: Record<Route['name'], { title: string; subtitle: string }> = {
  overview: { title: 'Overview', subtitle: 'Fleet health at a glance' },
  clients: { title: 'Clients', subtitle: 'The businesses you install for, and their sites' },
  devices: { title: 'Devices', subtitle: 'Appliances across every site' },
  users: { title: 'People', subtitle: 'Who has access, and what they can do' },
  audit: { title: 'Audit log', subtitle: 'Who did what, and when' },
  accept: { title: 'Accept invitation', subtitle: '' },
};

export function App(): React.JSX.Element {
  const [signedIn, setSignedIn] = useState(() => storedToken() !== null);
  const { devices, fetchState, unauthenticated, refresh } = useDevices(signedIn);

  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onHash = (): void => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  /*
   * Held until dismissed, never auto-hidden. The code exists in exactly one
   * response and cannot be retrieved afterwards, so a toast that faded while
   * someone reached for a pen would lose it.
   */
  const [issued, setIssued] = useState<IssuedClaim | null>(null);
  const [provisioning, setProvisioning] = useState(false);

  const [clientCount, setClientCount] = useState(0);
  const [siteCount, setSiteCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const loadCounts = useCallback(async () => {
    try {
      const [clients, sites] = await Promise.all([fetchClients(), fetchSites()]);
      setClientCount(clients.length);
      setSiteCount(sites.length);
    } catch {
      // Counts are decoration on the nav. Failing to load them must not take the
      // page down with them.
    }
  }, []);

  useEffect(() => {
    if (signedIn) void loadCounts();
  }, [signedIn, loadCounts, reloadKey]);

  const reloadAll = useCallback((): void => {
    setReloadKey((k) => k + 1);
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (unauthenticated) {
      storeToken(null);
      setSignedIn(false);
    }
  }, [unauthenticated]);

  /*
   * A clock for relative timestamps. Without it "last seen 4 seconds ago" freezes
   * between polls and only updates when the data does — which is exactly when it
   * is least trustworthy.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  /*
   * The invitation page comes before the session check, because someone
   * redeeming one has no account yet — that is the entire point. Every other
   * route falls through to the login form.
   */
  if (route.name === 'accept' && route.id) {
    return (
      <AcceptInvite
        inviteCode={route.id}
        onAccepted={() => {
          // The code is cleared from the address bar on the way to sign in, so a
          // spent invitation is not left sitting in browser history.
          window.location.hash = '';
          setRoute({ name: 'overview', id: null });
        }}
      />
    );
  }

  if (!signedIn) {
    return <Login onSignedIn={() => setSignedIn(true)} />;
  }

  // A signed-in operator following a stale invitation link has nothing to do
  // here; the app is what they wanted.
  if (isPublicRoute(route.name)) {
    window.location.hash = '';
  }

  const page = PAGE_TITLES[route.name];
  /*
   * The detail page names itself rather than repeating "Devices". The device's
   * own name is not known until it loads, so the heading stays generic — a
   * heading that flickered from "Device" to a name would be worse than one that
   * did not move.
   */
  const heading =
    route.name === 'devices' && route.id
      ? { title: 'Device', subtitle: 'Health, entitlement and cameras' }
      : page;

  return (
    <Shell
      active={route.name}
      counts={{ clients: clientCount, devices: devices.length }}
      title={heading.title}
      subtitle={heading.subtitle}
      onSignOut={() => {
        storeToken(null);
        setSignedIn(false);
      }}
      actions={
        route.name === 'devices' && !route.id ? (
          <button type="button" className="btn btn--primary" onClick={() => setProvisioning(true)}>
            Provision device
          </button>
        ) : null
      }
    >
      {issued ? (
        <ClaimCode
          code={issued.claimCode}
          expiresAt={issued.claimCodeExpiresAt}
          deviceName={issued.deviceName}
          now={now}
          onDismiss={() => setIssued(null)}
        />
      ) : null}

      {route.name === 'overview' ? (
        <Overview
          devices={devices}
          fetchState={fetchState}
          now={now}
          clientCount={clientCount}
          siteCount={siteCount}
        />
      ) : null}

      {route.name === 'clients' ? <Clients onChanged={reloadAll} /> : null}

      {route.name === 'users' ? <Users now={now} /> : null}

      {route.name === 'audit' ? <Audit now={now} /> : null}

      {route.name === 'devices' && route.id ? (
        <DeviceDetail
          deviceId={route.id}
          now={now}
          onChanged={reloadAll}
          onClaimIssued={setIssued}
        />
      ) : null}

      {route.name === 'devices' && !route.id ? (
        <DeviceList
          devices={devices}
          fetchState={fetchState}
          now={now}
          onChanged={reloadAll}
          onClaimIssued={setIssued}
        />
      ) : null}

      {/* Provisioning is an occasional action, so it lives behind a button
          rather than permanently above the fleet. */}
      {provisioning ? (
        <div
          className="panel-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setProvisioning(false);
          }}
        >
          <div className="panel-card card">
            <div className="panel-card__head">
              <h2>Provision a device</h2>
              <button type="button" className="btn btn--sm" onClick={() => setProvisioning(false)}>
                Close
              </button>
            </div>
            <div className="card__body">
              <Provision
                reloadKey={reloadKey}
                onChanged={reloadAll}
                onProvisioned={(result) => {
                  setIssued(result);
                  setProvisioning(false);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}
